import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import giveTakeService from '../services/giveTakeService';
import { useDialog } from '../context';
import TintSquare from './hand/TintSquare';
import { Panel, PanelHeader, PanelFooterButton, SurfaceButton, MUTED, SUBTLE, HAIRLINE, INSET, useSoft, SOFT } from './surface';

/**
 * GiveTakeBoard — a two-column Offerings / Asks board. A post can be claimed,
 * which opens a 1:1 chat (reuses the Messages system) and takes the post off the
 * board. Authors can edit/delete their own open posts. Posts expire on their own.
 *
 * Two looks via `variant`:
 *   "portal"    — the gold Portal Noir aesthetic (used inside .portal-scope).
 *   "brutalist" — the base black/white theme (gathering pages).
 *   "surface"   — the 2026-09 event-page kit: two side-by-side GIVE / TAKE panels,
 *                 38px square Resume avatars linking to /{handle}, name + meta,
 *                 footer buttons add a row (handoff README §1 GIVE / TAKE).
 * Defaults to "portal" when context==="portal", else "brutalist".
 *
 * Props: context ("portal"|"event"|"gathering"), eventId?, gatheringId?,
 * variant?, mobile? (surface only), onNeedsLocation? (portal only — called when
 * viewer has no location).
 */
export default function GiveTakeBoard({
  context = 'portal',
  eventId = null,
  gatheringId = null,
  variant,
  mobile = false,
  onNeedsLocation,
}) {
  const navigate = useNavigate();
  const { showConfirm, showAlert } = useDialog();
  const isPortal = (variant || (context === 'portal' ? 'portal' : 'brutalist')) === 'portal';
  const isSurface = variant === 'surface';
  const soft = useSoft();

  const [board, setBoard] = useState({ offers: [], asks: [], needsLocation: false, locationLabel: null, radiusMiles: 50 });
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(null); // { kind, editingId, title, note }
  const [busy, setBusy] = useState(false);
  // Portal Noir board is international by default; local-only applies the radius filter.
  const [localOnly, setLocalOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await giveTakeService.getBoard({ context, eventId, gatheringId, localOnly });
    if (res.success) setBoard(res.data);
    setLoading(false);
    if (res.success && res.data.needsLocation && onNeedsLocation) onNeedsLocation();
  }, [context, eventId, gatheringId, localOnly, onNeedsLocation]);

  useEffect(() => { load(); }, [load]);

  const openComposer = (kind) => setComposer({ kind, editingId: null, title: '', note: '' });
  const openEdit = (post) => setComposer({ kind: post.kind, editingId: post.id, title: post.title, note: post.note || '' });
  const closeComposer = () => setComposer(null);

  const submitComposer = async () => {
    const title = (composer.title || '').trim();
    if (!title) return;
    setBusy(true);
    let res;
    if (composer.editingId) {
      res = await giveTakeService.updatePost(composer.editingId, { title, note: composer.note || null });
    } else {
      res = await giveTakeService.createPost({ context, eventId, gatheringId, kind: composer.kind, title, note: composer.note || null });
    }
    setBusy(false);
    if (!res.success) { await showAlert({ title: 'Could not post', message: res.error?.message || 'Something went wrong.', type: 'error' }); return; }
    setComposer(null);
    load();
  };

  const claim = async (post) => {
    setBusy(true);
    const res = await giveTakeService.claimPost(post.id);
    setBusy(false);
    if (!res.success) { await showAlert({ title: 'Could not claim', message: res.error?.message || 'Something went wrong.', type: 'error' }); load(); return; }
    navigate(`/messages?thread=${res.data.threadId}`);
  };

  const remove = async (post) => {
    const ok = await showConfirm({ title: 'Delete this post?', message: post.title, confirmLabel: 'Delete', cancelLabel: 'Keep', variant: 'danger' });
    if (!ok) return;
    const res = await giveTakeService.deletePost(post.id);
    if (!res.success) { await showAlert({ title: 'Could not delete', message: res.error?.message || 'Something went wrong.', type: 'error' }); return; }
    load();
  };

  const openThread = (post) => { if (post.threadId) navigate(`/messages?thread=${post.threadId}`); };

  // Author "rejects" a claim (fell through) or claimer "unclaims" — both reopen it.
  const unclaim = async (post) => {
    const mine = post.isMine;
    const ok = await showConfirm({
      title: mine ? 'Reject this claim?' : 'Unclaim this?',
      message: mine ? `Put "${post.title}" back on the board for someone else?` : `Give "${post.title}" back to the board?`,
      confirmLabel: mine ? 'Reject' : 'Unclaim',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    const res = await giveTakeService.unclaimPost(post.id);
    if (!res.success) { await showAlert({ title: 'Could not update', message: res.error?.message || 'Something went wrong.', type: 'error' }); return; }
    load();
  };

  const claimCta = (kind) => (kind === 'offer' ? "I'll take you up on it" : "I've got this");
  const expiryLabel = (post) => {
    const left = new Date(post.expiresAt).getTime() - Date.now();
    if (left <= 0) return null;
    const h = left / 3.6e6;
    if (h >= 48) return `Expires ${Math.round(h / 24)}d`;
    return h >= 1 ? `Expires in ${Math.round(h)}h` : 'Expires soon';
  };

  if (isPortal) {
    return renderPortal();
  }
  if (isSurface) {
    return renderSurface();
  }
  return renderBrutalist();

  // ---------------------------------------------------------------- SURFACE
  function renderSurface() {
    if (loading) {
      return <div className="font-mono" style={{ padding: 16, fontSize: 12, color: MUTED }} data-testid="give-take-loading">Loading the board…</div>;
    }
    return (
      <div className={mobile ? 'flex flex-col gap-3' : 'grid grid-cols-2 gap-4'} data-testid="give-take-surface">
        {renderSurfaceColumn('offer', 'GIVE', board.offers, 'OFFER', '+ OFFER SOMETHING')}
        {renderSurfaceColumn('ask', 'TAKE', board.asks, 'ASK', '+ ASK FOR SOMETHING')}
      </div>
    );
  }

  function renderSurfaceColumn(kind, label, posts, noun, addLabel) {
    const adding = composer && composer.kind === kind && !composer.editingId;
    const open = posts.filter((p) => p.status === 'open').length;
    return (
      <Panel data-testid={`board-${kind}`}>
        <PanelHeader label={label} right={`${open} ${open === 1 ? noun : `${noun}S`}`} dense={mobile} />
        {adding && renderSurfaceForm(kind)}
        <div className="flex flex-col">
          {posts.length === 0 && !adding && (
            <div className="font-mono" style={{ padding: soft ? 0 : mobile ? 12 : 16, fontSize: 12, color: MUTED }}>{kind === 'offer' ? 'Nothing on offer yet.' : 'No asks right now.'}</div>
          )}
          {posts.map((p, i) => (
            composer && composer.editingId === p.id
              ? <div key={p.id}>{renderSurfaceForm(kind)}</div>
              : renderSurfaceRow(p, i === posts.length - 1)
          ))}
        </div>
        <PanelFooterButton onClick={() => (adding ? closeComposer() : openComposer(kind))} data-testid={`board-add-${kind}`}>{adding ? 'CANCEL' : addLabel}</PanelFooterButton>
      </Panel>
    );
  }

  function renderSurfaceForm(kind) {
    const give = kind === 'offer';
    const field = soft
      ? 'w-full font-mono text-black placeholder:text-gray-400 focus:outline-none'
      : 'w-full border-2 border-black bg-white font-mono text-black placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black';
    const fieldStyle = soft ? { background: '#fff', border: '2px solid transparent', borderRadius: 999, height: 44, padding: '0 16px', fontSize: 13 } : { padding: '10px 12px', fontSize: 13 };
    return (
      <div className={`flex flex-col gap-2 ${soft ? '' : 'border-b-2 border-black'}`} style={soft ? { padding: 14, background: SOFT.canvas, borderRadius: 18 } : { padding: mobile ? 12 : 16, background: INSET }} data-testid={`board-form-${kind}`}>
        <input className={field} style={fieldStyle} autoFocus value={composer.title} onChange={(e) => setComposer({ ...composer, title: e.target.value })} placeholder={give ? 'What are you offering?' : 'What do you need?'} />
        <textarea className={field} style={soft ? { ...fieldStyle, height: 'auto', borderRadius: 16, padding: '10px 16px', minHeight: 64, resize: 'vertical' } : { padding: '10px 12px', fontSize: 13, minHeight: 64, resize: 'vertical' }} value={composer.note} onChange={(e) => setComposer({ ...composer, note: e.target.value })} placeholder="Add a line of detail (optional)." />
        <div className="flex justify-end gap-2">
          <SurfaceButton variant="outline" size="xs" onClick={closeComposer}>CANCEL</SurfaceButton>
          <SurfaceButton variant="fill" size="xs" disabled={busy || !composer.title.trim()} onClick={submitComposer}>
            {composer.editingId ? 'SAVE' : give ? 'POST OFFER' : 'POST ASK'}
          </SurfaceButton>
        </div>
      </div>
    );
  }

  function renderSurfaceRow(p, last) {
    const claimed = p.status === 'claimed';
    const meta = claimed
      ? (p.isClaimedByMe ? 'claimed · yours' : p.isMine && p.claimedByName ? `claimed by ${p.claimedByName}` : 'claimed')
      : (expiryLabel(p) || '').toLowerCase();
    const size = mobile ? 32 : 38;
    const avatar = <TintSquare name={p.authorName} imageUrl={p.authorAvatarUrl} size={size} tintKey={p.authorHandle || p.authorName} />;
    const link = 'font-mono text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-black';
    return (
      <div key={p.id} className="flex items-start gap-3" style={{ padding: soft ? '11px 0' : mobile ? '9px 12px' : '11px 16px', borderBottom: last ? 'none' : `2px solid ${soft ? SOFT.chip : HAIRLINE}`, opacity: claimed ? 0.6 : 1 }} data-testid={`board-row-${p.id}`}>
        {p.authorHandle ? <Link to={`/${p.authorHandle}`} className="flex-shrink-0" aria-label={`${p.authorName} — view Portal Page`}>{avatar}</Link> : avatar}
        <div className="min-w-0 flex-1">
          <div className="font-mono" style={{ fontSize: mobile ? 12 : 13, lineHeight: 1.45 }}>{p.title}</div>
          {p.note && <div className="font-mono" style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>{p.note}</div>}
          <div className="font-mono" style={{ fontSize: mobile ? 10 : 11, color: MUTED, marginTop: 2 }}>
            {(p.authorName || '').toUpperCase()}
            {meta && <span style={{ color: SUBTLE }}> · {meta}</span>}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {p.isMine ? (
              <>
                {!claimed && <button type="button" className={link} onClick={() => openEdit(p)}>Edit</button>}
                {!claimed && <button type="button" className={link} onClick={() => remove(p)}>Delete</button>}
                {claimed && p.threadId && <button type="button" className={`${link} underline`} onClick={() => openThread(p)}>Open thread</button>}
                {claimed && <button type="button" className={link} onClick={() => unclaim(p)}>Reject</button>}
                {claimed && <button type="button" className="font-mono text-[10px] font-bold uppercase tracking-wider text-red-600 hover:text-red-800" onClick={() => remove(p)}>Delete</button>}
              </>
            ) : claimed ? (
              <>
                {p.isClaimedByMe && p.threadId && <button type="button" className={`${link} underline`} onClick={() => openThread(p)}>Open thread</button>}
                {p.isClaimedByMe && <button type="button" className={link} onClick={() => unclaim(p)}>Unclaim</button>}
              </>
            ) : (
              <SurfaceButton variant="fill" size="xs" disabled={busy} onClick={() => claim(p)} data-testid={`board-claim-${p.id}`}>{claimCta(p.kind).toUpperCase()}</SurfaceButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------- PORTAL NOIR
  function renderPortalScope() {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button className={'btn btn--sm ' + (!localOnly ? 'btn--gold' : 'btn--ghost')} onClick={() => setLocalOnly(false)}>International</button>
        <button className={'btn btn--sm ' + (localOnly ? 'btn--gold' : 'btn--ghost')} onClick={() => setLocalOnly(true)}>Local only</button>
        {localOnly && board.locationLabel && (
          <span className="kicker" style={{ opacity: 0.7 }}>Within {board.radiusMiles} mi of {board.locationLabel}</span>
        )}
      </div>
    );
  }

  function renderPortal() {
    return (
      <>
        {renderPortalScope()}
        {board.needsLocation ? (
          <div className="gtempty">Set your current location to see local give-takes, or switch to International<span className="dot">.</span></div>
        ) : (
          <div className="gtboard">
            {renderPortalColumn('offer', 'Offerings', board.offers)}
            {renderPortalColumn('ask', 'Asks', board.asks)}
          </div>
        )}
      </>
    );
  }

  function renderPortalColumn(kind, label, posts) {
    const adding = composer && composer.kind === kind && !composer.editingId;
    return (
      <div className="gtcol">
        <div className="gthead gthead--give">
          <span className="kicker kicker--gold">{label}</span>
          <button className="gtcount gtcount--give gtadd" aria-label={`Add ${label}`} onClick={() => (adding ? closeComposer() : openComposer(kind))}>+</button>
        </div>
        {adding && renderPortalForm(kind)}
        {posts.length === 0 && !adding && (
          <div className="gtempty">{kind === 'offer' ? 'No offerings live right now. Be the first' : 'No asks live right now. Say what you need'}<span className="dot">.</span></div>
        )}
        {posts.map((p) => (
          composer && composer.editingId === p.id
            ? <div key={p.id}>{renderPortalForm(kind)}</div>
            : renderPortalCard(p)
        ))}
      </div>
    );
  }

  function renderPortalForm(kind) {
    const give = kind === 'offer';
    return (
      <div className="gtform">
        <span className={'kicker' + (give ? ' kicker--gold' : '')}>{composer.editingId ? 'Edit your post' : give ? 'New offering' : 'New ask'}</span>
        <p className="gtform__hint">Clears in 24 hours, or the moment someone claims it.</p>
        <input className="input" autoFocus value={composer.title} onChange={(e) => setComposer({ ...composer, title: e.target.value })} placeholder={give ? 'What are you offering?' : 'What do you need?'} />
        <textarea className="textarea" style={{ minHeight: 92 }} value={composer.note} onChange={(e) => setComposer({ ...composer, note: e.target.value })} placeholder="Add a line of detail (optional)." />
        <div className="gtform__row">
          <button className="btn btn--ghost btn--sm" onClick={closeComposer}>Cancel</button>
          <button className="btn btn--gold btn--sm" disabled={busy || !composer.title.trim()} onClick={submitComposer}>
            {composer.editingId ? 'Save changes' : give ? 'Post the offer' : 'Post the ask'}
          </button>
        </div>
      </div>
    );
  }

  function renderPortalCard(p) {
    const claimed = p.status === 'claimed';
    const meta = [p.distanceMiles != null ? `${p.distanceMiles} mi` : null, p.locationLabel].filter(Boolean).join(' · ');
    return (
      <div key={p.id} className={'gtcard gtcard--give' + (claimed ? ' gtcard--done' : '')}>
        <span className="gtcard__seam" />
        <div className="gtcard__top">
          <span className="gtcard__who">{p.authorName}</span>
          <span className={'tag' + (claimed ? '' : ' tag--open')}>{claimed ? 'Claimed' : 'Open'}</span>
        </div>
        <div className="gtcard__title">{p.title}</div>
        {p.note && <div className="gtcard__note">{p.note}</div>}
        {meta && <div className="gtcard__who" style={{ marginTop: 10 }}>{meta}</div>}
        <div className="gtcard__foot2">
          {p.isMine ? (
            <div className="gtcard__actions">
              {!claimed && <button className="gtlink" onClick={() => openEdit(p)}>Edit</button>}
              {!claimed && <button className="gtlink" onClick={() => remove(p)}>Delete</button>}
              {claimed && p.threadId && <button className="gtlink" onClick={() => openThread(p)}>Open thread</button>}
              {claimed && <button className="gtlink" onClick={() => unclaim(p)}>Reject</button>}
              {claimed && <button className="gtlink" onClick={() => remove(p)}>Delete</button>}
            </div>
          ) : claimed ? (
            <div className="gtcard__actions">
              <span className="gtmeta-claimed">{p.isClaimedByMe ? 'Claimed · yours' : 'Claimed'}</span>
              {p.isClaimedByMe && p.threadId && <button className="gtlink" onClick={() => openThread(p)}>Open thread</button>}
              {p.isClaimedByMe && <button className="gtlink" onClick={() => unclaim(p)}>Unclaim</button>}
            </div>
          ) : (
            <button className="btn btn--gold btn--sm gtclaim" disabled={busy} onClick={() => claim(p)}>{claimCta(p.kind)} <span className="arr">→</span></button>
          )}
          {!claimed && expiryLabel(p) && <span className="gtmeta-expire">{expiryLabel(p)}</span>}
        </div>
      </div>
    );
  }

  // ------------------------------------------------------------- BRUTALIST
  function renderBrutalist() {
    if (loading) {
      return <div className="font-mono text-sm text-gray-500 p-4">Loading…</div>;
    }
    const offerCount = board.offers.filter((p) => p.status === 'open').length;
    const askCount = board.asks.filter((p) => p.status === 'open').length;
    return (
      <div className="border-2 border-black">
        <div className="bg-black text-white p-5 flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-xs font-semibold uppercase tracking-wider">Give / Take</div>
            <p className="font-mono text-xs text-gray-300 mt-2 max-w-md leading-relaxed">What people are offering, and what they need. Claim it and you sort the rest in a private chat.</p>
          </div>
          <div className="border-2 border-white/30 px-3 py-2 text-right shrink-0">
            <div className="font-mono text-[10px] uppercase tracking-wider text-gray-400">Offers / Asks</div>
            <div className="font-mono text-2xl font-semibold mt-1">{offerCount}<span className="text-gray-500"> / </span>{askCount}</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          {renderBrutalistColumn('offer', 'Offerings', board.offers, 'md:border-r-2 md:border-black')}
          {renderBrutalistColumn('ask', 'Asks', board.asks, 'border-t-2 md:border-t-0 border-black')}
        </div>
      </div>
    );
  }

  function renderBrutalistColumn(kind, label, posts, extra) {
    const adding = composer && composer.kind === kind && !composer.editingId;
    return (
      <div className={extra}>
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black">
          <span className="font-mono text-xs font-semibold uppercase tracking-wider">{label}</span>
          <button className="font-mono text-xs font-semibold uppercase tracking-wider border-2 border-black px-3 py-1 hover:bg-black hover:text-white" onClick={() => (adding ? closeComposer() : openComposer(kind))}>
            {kind === 'offer' ? '+ I offer' : '+ I need'}
          </button>
        </div>
        {adding && renderBrutalistForm(kind)}
        {posts.length === 0 && !adding && (
          <div className="font-mono text-sm text-gray-500 p-5 leading-relaxed">{kind === 'offer' ? 'Nothing on offer yet.' : 'No asks right now.'}</div>
        )}
        {posts.map((p) => (
          composer && composer.editingId === p.id
            ? <div key={p.id}>{renderBrutalistForm(kind)}</div>
            : renderBrutalistCard(p)
        ))}
      </div>
    );
  }

  function renderBrutalistForm(kind) {
    const give = kind === 'offer';
    return (
      <div className="p-4 border-b-2 border-black bg-gray-50">
        <input className="w-full px-3 py-2 border-2 border-black bg-white text-black font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black" autoFocus value={composer.title} onChange={(e) => setComposer({ ...composer, title: e.target.value })} placeholder={give ? 'What are you offering?' : 'What do you need?'} />
        <textarea className="w-full mt-2 px-3 py-2 border-2 border-black bg-white text-black font-mono text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black" style={{ minHeight: 64, resize: 'vertical' }} value={composer.note} onChange={(e) => setComposer({ ...composer, note: e.target.value })} placeholder="Add a line of detail (optional)." />
        <div className="flex justify-end gap-2 mt-3">
          <button className="font-mono text-xs font-semibold uppercase tracking-wider px-3 py-2 text-gray-500 hover:text-black" onClick={closeComposer}>Cancel</button>
          <button className="font-mono text-xs font-semibold uppercase tracking-wider border-2 border-black bg-black text-white px-4 py-2 hover:bg-white hover:text-black disabled:opacity-40" disabled={busy || !composer.title.trim()} onClick={submitComposer}>
            {composer.editingId ? 'Save' : give ? 'Post offer' : 'Post ask'}
          </button>
        </div>
      </div>
    );
  }

  function renderBrutalistCard(p) {
    const claimed = p.status === 'claimed';
    return (
      <div key={p.id} className={'px-4 py-4 border-b border-gray-200' + (claimed ? ' opacity-60' : '')}>
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-600">{p.authorName}</span>
          <span className={'font-mono text-[10px] uppercase tracking-wider border-2 px-2 py-0.5 ' + (claimed ? 'border-gray-400 text-gray-500' : 'border-black text-black')}>{claimed ? 'Claimed' : 'Open'}</span>
        </div>
        <div className="font-mono font-semibold mt-2">{p.title}</div>
        {p.note && <p className="font-mono text-sm text-gray-700 mt-1 leading-relaxed">{p.note}</p>}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {p.isMine ? (
            <>
              {!claimed && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-black" onClick={() => openEdit(p)}>Edit</button>}
              {!claimed && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-black" onClick={() => remove(p)}>Delete</button>}
              {claimed && p.threadId && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider underline" onClick={() => openThread(p)}>Open thread</button>}
              {claimed && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-black" onClick={() => unclaim(p)}>Reject</button>}
              {claimed && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-red-600 hover:text-red-800" onClick={() => remove(p)}>Delete</button>}
            </>
          ) : claimed ? (
            <>
              <span className="font-mono text-[11px] uppercase tracking-wider text-gray-500">{p.isClaimedByMe ? 'Claimed · yours' : 'Claimed'}</span>
              {p.isClaimedByMe && p.threadId && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider underline" onClick={() => openThread(p)}>Open thread</button>}
              {p.isClaimedByMe && <button className="font-mono text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-black" onClick={() => unclaim(p)}>Unclaim</button>}
            </>
          ) : (
            <button className="font-mono text-[11px] font-semibold uppercase tracking-wider border-2 border-black bg-black text-white px-3 py-1.5 hover:bg-white hover:text-black disabled:opacity-40" disabled={busy} onClick={() => claim(p)}>{claimCta(p.kind)}</button>
          )}
        </div>
      </div>
    );
  }
}
